(() => {
  if (window.PSMQuickBuyV9Loaded) return;
  window.PSMQuickBuyV9Loaded = true;

  const REQUIRED_ORDER = [
    'week',
    '2 weeks',
    '3 weeks',
    'month',
    '6 weeks',
    '2 months',
    '3 months',
    '4 months',
    '5 months',
    '6 months',
    '7 months',
    '8 months'
  ];

  const normaliseFrequency = (value) => {
    const normalised = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/^every\s+/, '');

    if (normalised === '1 week') return 'week';
    if (normalised === '1 month') return 'month';
    return normalised;
  };

  const frequencyLabel = (value) => {
    const normalised = normaliseFrequency(value);
    if (normalised === 'week') return '1 Week';
    if (normalised === 'month') return '1 Month';

    return String(value || '')
      .replace(/^every\s+/i, '')
      .split(' ')
      .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : word)
      .join(' ');
  };

  class PSMQuickBuy extends HTMLElement {
    connectedCallback() {
      /*
       * Shopify's modal code moves this modal into <body> the first time it opens.
       * That disconnects/reconnects this custom element. Re-bind document listeners
       * on reconnect instead of returning with them detached.
       */
      if (this.dataset.initialised === 'true') {
        /* Shopify may move the modal into <body> when it opens. All listeners
           attached directly to this component survive that move, so there is
           nothing to re-bind here. */
        return;
      }
      this.dataset.initialised = 'true';

      const jsonNode = this.querySelector('[data-psm-qb-json]');
      if (!jsonNode) return;

      try {
        this.data = JSON.parse(jsonNode.textContent);
      } catch (error) {
        console.error('Pet Supermarket quick buy: product data could not be read.', error);
        return;
      }

      this.variantField = this.querySelector('[data-psm-qb-variant-field]');
      this.variantSelect = this.querySelector('[data-psm-qb-variant]');
      this.variantError = this.querySelector('[data-psm-qb-variant-error]');
      this.variantInput = this.querySelector('[data-psm-qb-variant-input]');
      this.availability = this.querySelector('[data-psm-qb-availability]');
      this.sellingPlanInput = this.querySelector('[data-psm-qb-selling-plan]');
      this.quantityInput = this.querySelector('[data-psm-qb-quantity]');
      this.quantityWrap = this.querySelector('[data-psm-qb-quantity-wrap]');
      this.quantityMinus = this.querySelector('[data-psm-qb-quantity-minus]');
      this.quantityPlus = this.querySelector('[data-psm-qb-quantity-plus]');
      this.quantityStock = this.querySelector('[data-psm-qb-quantity-stock]');
      this.quantityError = this.querySelector('[data-psm-qb-quantity-error]');
      this.frequencyWrap = this.querySelector('[data-psm-qb-frequency-wrap]');
      this.frequencySelect = this.querySelector('[data-psm-qb-frequency]');
      this.frequencyPopularHint = this.querySelector('[data-psm-qb-popular-hint]');
      this.frequencyError = this.querySelector('[data-psm-qb-frequency-error]');
      this.purchaseOptions = this.querySelector('[data-psm-qb-purchase-options]');
      this.oneTimeChoice = this.querySelector('[data-psm-qb-choice="one-time"]');
      this.subscriptionChoice = this.querySelector('[data-psm-qb-choice="subscription"]');
      this.oneTimeChoicePrice = this.querySelector('[data-psm-qb-one-time-choice-price]');
      this.subscriptionChoicePrice = this.querySelector('[data-psm-qb-sub-choice-price]');
      this.subscriptionChoiceSaving = this.querySelector('[data-psm-qb-sub-choice-saving]');
      this.subscriptionSave = this.querySelector('[data-psm-qb-save-percent]');
      this.summaryRow = this.querySelector('[data-psm-qb-summary]');
      this.summaryPrice = this.querySelector('[data-psm-qb-price]');
      this.summaryOriginal = this.querySelector('[data-psm-qb-original]');
      this.summarySaving = this.querySelector('[data-psm-qb-saving]');
      this.image = this.querySelector('[data-psm-qb-image]');
      this.addButton = this.querySelector('[data-psm-qb-add]');
      this.addButtonText = this.querySelector('[data-psm-qb-add-text]');
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.modal = this.closest('modal-dialog');

      this.requiresVariantSelection = this.data.requires_variant_selection === true;
      this.mode = this.data.has_subscription ? 'subscription' : 'one-time';
      this.currentVariant = null;
      this.currentAllocation = null;
      this.currentPlanId = '';
      this.variantHasSubscription = false;
      this.pendingVariantId = null;
      this.quantityRules = { min: 1, max: 1, step: 1 };
      this.isSubmitting = false;

      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', () => {
          if (!this.variantSelect.value) {
            this.clearVariant();
            return;
          }
          this.setVariant(this.variantSelect.value, true);
        });
      }

      this.querySelectorAll('[data-psm-qb-choice]').forEach((choice) => {
        choice.addEventListener('click', () => {
          if (!this.currentVariant) return;
          this.setMode(choice.dataset.psmQbChoice);
        });
      });

      if (this.frequencySelect) {
        this.frequencySelect.addEventListener('change', () => {
          if (!this.currentVariant || this.mode !== 'subscription') return;

          if (!this.frequencySelect.value) {
            this.currentPlanId = '';
            this.currentAllocation = null;
            this.clearSellingPlan();
            this.setFrequencyRequired(true);
            this.updateSubscriptionChoicePreview();
            this.updatePriceSummary();
            this.updateButtonState();
            return;
          }

          this.selectFrequency(this.frequencySelect.value);
        });
      }

      if (this.quantityMinus) {
        this.quantityMinus.addEventListener('click', () => {
          if (!this.currentVariant) return;
          this.setQuantity(Number(this.quantityInput?.value || this.quantityRules.min) - this.quantityRules.step);
        });
      }

      if (this.quantityPlus) {
        this.quantityPlus.addEventListener('click', () => {
          if (!this.currentVariant) return;
          const current = Number(this.quantityInput?.value || this.quantityRules.min);
          if (current >= this.quantityRules.max) {
            this.showQuantityError(`Maximum quantity available is ${this.quantityRules.max}.`);
            return;
          }
          this.setQuantity(current + this.quantityRules.step);
        });
      }

      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', () => {
          if (!this.currentVariant) return;
          this.setQuantity(this.quantityInput.value, true);
        });
      }

      if (this.form) {
        /*
         * This Quick Buy submits its own AJAX cart request.
         *
         * The theme's normal <product-form> is deliberately lazy-initialised.
         * Because our product form lives inside a modal that starts hidden, that
         * initializer is not guaranteed to have run before the customer clicks
         * Add to basket. That was the cause of the endless loading state: our
         * overlay started, but no cart request existed underneath it.
         *
         * Intercepting this one form keeps the custom Quick Buy self-contained
         * and does NOT alter the PDP product form.
         */
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();

          if (this.isSubmitting) return;

          if (!this.validateBeforeSubmit()) return;

          if (this.mode === 'subscription') {
            this.applySellingPlan();
          } else {
            this.clearSellingPlan();
          }

          this.pendingVariantId = String(this.currentVariant.id);
          this.submitToCart();
        }, true);
      }

      if (this.modal?.id) {
        this.opener = document.querySelector(`[aria-controls="${CSS.escape(this.modal.id)}"]`);
        this.opener?.addEventListener('click', () => this.reset());
      }

      this.reset();
    }

    clearCartError() {
      if (!this.form) return;
      const wrapper = this.form.querySelector('.product-form__error-message-wrapper');
      const message = this.form.querySelector('.product-form__error-message');
      if (wrapper) wrapper.hidden = true;
      if (message) message.textContent = '';
    }

    showCartError(errorMessage) {
      if (!this.form) return;
      const wrapper = this.form.querySelector('.product-form__error-message-wrapper');
      const message = this.form.querySelector('.product-form__error-message');
      if (message) {
        message.textContent = errorMessage || 'Sorry, this item could not be added to your basket. Please try again.';
      }
      if (wrapper) {
        wrapper.hidden = false;
        wrapper.setAttribute('role', 'alert');
      }
    }

    async submitToCart() {
      if (!this.form || !this.currentVariant || this.isSubmitting) return;

      this.clearCartError();
      this.setSubmitting(true);

      const submittedVariantId = String(this.currentVariant.id);
      const submittedQuantity = Number(this.quantityInput?.value || 1);
      const cartDrawer = document.querySelector('cart-drawer');
      const cartItems = document.querySelector('cart-items');
      const cartNotification = document.querySelector('cart-notification');

      try {
        /* Ensure deferred cart markup exists before asking it which sections
           need to be returned by Shopify. */
        if (cartDrawer && typeof cartDrawer.init === 'function') {
          cartDrawer.init();
        }

        if (cartNotification && typeof cartNotification.init === 'function') {
          cartNotification.init();
        }

        const formData = new FormData(this.form);
        const sections = [];

        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          cartDrawer.getSectionsToRender().forEach((section) => sections.push(section.id));
        }

        if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
          cartNotification.getSectionsToRender().forEach((section) => sections.push(section.id));
        }

        if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
          cartItems.getSectionsToRender().forEach((section) => sections.push(section.section || section.id));
        }

        if (sections.length) {
          formData.append('sections', [...new Set(sections.filter(Boolean))]);
          formData.append('sections_url', window.location.pathname);
        }

        if (cartDrawer && typeof cartDrawer.setActiveElement === 'function') {
          cartDrawer.setActiveElement(this.opener || document.activeElement);
        }

        if (cartNotification && typeof cartNotification.setActiveElement === 'function') {
          cartNotification.setActiveElement(this.opener || document.activeElement);
        }

        const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript'
          },
          body: formData
        });

        let cartData;
        try {
          cartData = await response.json();
        } catch (parseError) {
          throw new Error('Shopify returned an unexpected response. Please try again.');
        }

        if (!response.ok || cartData?.status) {
          const description =
            cartData?.description ||
            cartData?.message ||
            (typeof cartData?.errors === 'string' ? cartData.errors : '') ||
            'Sorry, this item could not be added to your basket. Please try again.';

          this.showCartError(description);
          this.pendingVariantId = null;
          this.setSubmitting(false);
          return;
        }

        /* Keep the remaining-stock helper accurate if this same Quick Buy is
           reopened before the collection page is refreshed. */
        if (this.currentVariant) {
          this.currentVariant.cart_quantity =
            Number(this.currentVariant.cart_quantity || 0) + submittedQuantity;
        }

        this.pendingVariantId = null;

        /* Broadcast the same cart update the theme's normal product form uses,
           so header/cart listeners and any other integrations stay in sync. */
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: submittedVariantId,
              cartData
            });
          } else {
            document.dispatchEvent(new CustomEvent('cart-add', {
              detail: { variantId: submittedVariantId, cartData }
            }));
          }
        } catch (eventError) {
          console.warn('Pet Supermarket quick buy: cart update event could not be published.', eventError);
        }

        /* Close THIS modal first. Then hand the successful Shopify response to
           the existing cart drawer/notification so the normal slide-out basket
           opens exactly as it does from the PDP. */
        this.setSubmitting(false);
        if (this.modal && typeof this.modal.hide === 'function') {
          this.modal.hide(true);
        } else if (this.modal) {
          this.modal.removeAttribute('open');
          document.body.classList.remove('overflow-hidden');
        }

        if (window.theme?.settings?.cartDrawer?.atcAction === 'page') {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && cartData.sections) {
          if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(cartData);
        }

        if (cartItems && typeof cartItems.renderContents === 'function' && cartData.sections) {
          cartItems.renderContents(cartData);
        }

        if (cartNotification && typeof cartNotification.renderContents === 'function' && cartData.sections) {
          if (cartNotification.classList.contains('is-empty')) cartNotification.classList.remove('is-empty');
          cartNotification.renderContents(cartData);
        }

        /* If the store is configured for the drawer but section rendering was
           unavailable for any reason, reload the drawer rather than leaving the
           customer wondering whether the item was added. */
        if (
          window.theme?.settings?.cartDrawer?.atcAction === 'drawer' &&
          cartDrawer &&
          !cartData.sections &&
          typeof cartDrawer.reload === 'function'
        ) {
          cartDrawer.reload();
          if (typeof cartDrawer.open === 'function') cartDrawer.open();
        }
      } catch (error) {
        console.error('Pet Supermarket quick buy: add to basket failed.', error);
        this.pendingVariantId = null;
        this.showCartError(error?.message || 'Sorry, this item could not be added to your basket. Please try again.');
        this.setSubmitting(false);
      }
    }

    setSubmitting(active) {
      this.isSubmitting = Boolean(active);
      this.classList.toggle('is-submitting', this.isSubmitting);
      this.modal?.classList.toggle('is-submitting', this.isSubmitting);
      this.setAttribute('aria-busy', this.isSubmitting ? 'true' : 'false');

      if (this.addButton) {
        if (this.isSubmitting) {
          this.addButton.setAttribute('aria-disabled', 'true');
          this.addButton.disabled = true;
        } else {
          this.updateButtonState();
        }
      }
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function' && this.data.money_format) {
        return Shopify.formatMoney(Number(cents || 0), this.data.money_format);
      }

      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: this.data.currency || 'GBP'
      }).format(Number(cents || 0) / 100);
    }

    findVariant(id) {
      return (this.data.variants || []).find((variant) => String(variant.id) === String(id));
    }

    ordergrooveAllocations(variant) {
      if (!variant) return [];
      const planIds = new Set((this.data.plans || []).map((plan) => String(plan.id)));
      return (variant.allocations || []).filter((allocation) => planIds.has(String(allocation.plan_id)));
    }

    sortedPlans() {
      return (this.data.plans || []).slice().sort((a, b) => {
        const aValue = normaliseFrequency(a.frequency || a.name);
        const bValue = normaliseFrequency(b.frequency || b.name);
        const ai = REQUIRED_ORDER.indexOf(aValue);
        const bi = REQUIRED_ORDER.indexOf(bValue);

        if (ai === -1 && bi === -1) return aValue.localeCompare(bValue);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
    }

    fallbackSubscriptionPrice(variant) {
      if (!variant) return 0;

      const discountedAllocation = this.ordergrooveAllocations(variant)
        .map((allocation) => Number(allocation.price || 0))
        .find((price) => price > 0 && price < Number(variant.price || 0));

      if (discountedAllocation) return discountedAllocation;

      const percent = Number(this.data.subscription_discount_percent || 10);
      return Math.round(Number(variant.price || 0) * (100 - percent) / 100);
    }

    priceForPlan(variant, planId) {
      if (!variant) return 0;
      const allocation = this.ordergrooveAllocations(variant)
        .find((item) => String(item.plan_id) === String(planId));
      const allocationPrice = Number(allocation?.price || 0);

      if (allocationPrice > 0 && allocationPrice < Number(variant.price || 0)) {
        return allocationPrice;
      }

      return this.fallbackSubscriptionPrice(variant);
    }

    savingDetails(regularPrice, subscriptionPrice) {
      const regular = Number(regularPrice || 0);
      const repeat = Number(subscriptionPrice || regular);
      const saving = Math.max(regular - repeat, 0);
      const percent = regular > 0 ? Math.round((saving / regular) * 100) : 0;
      return { saving, percent };
    }

    quantityRulesForVariant(variant) {
      if (!variant) return { min: 1, max: 0, step: 1 };

      const min = Math.max(1, Number(variant.min_quantity || 1));
      const step = Math.max(1, Number(variant.quantity_increment || 1));
      const cartQuantity = Math.max(0, Number(variant.cart_quantity || 0));

      /* Match the existing PDP rule: default maximum 20, but when Shopify
         controls stock and overselling is disabled, use actual remaining stock. */
      let max = 20;

      if (variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue') {
        max = Number(variant.inventory_quantity || 0) - cartQuantity;
      }

      if (variant.max_quantity !== null && variant.max_quantity !== undefined && variant.max_quantity !== '') {
        max = Math.min(max, Number(variant.max_quantity) - cartQuantity);
      }

      max = Math.max(0, max);

      if (max < min) {
        max = 0;
      } else {
        /* Ensure the maximum itself is a valid quantity-rule increment. */
        max = min + Math.floor((max - min) / step) * step;
      }

      return { min, max, step };
    }

    applyQuantityRules(variant) {
      this.quantityRules = this.quantityRulesForVariant(variant);

      if (this.quantityWrap) this.quantityWrap.hidden = !variant;
      if (!this.quantityInput) return;

      this.quantityInput.min = this.quantityRules.max > 0 ? this.quantityRules.min : 0;
      this.quantityInput.max = this.quantityRules.max;
      this.quantityInput.step = this.quantityRules.step;

      const startingQuantity = this.quantityRules.max > 0
        ? Math.min(this.quantityRules.min, this.quantityRules.max)
        : 0;

      this.setQuantity(startingQuantity);

      if (this.quantityStock) {
        if (this.quantityRules.max <= 0) {
          this.quantityStock.textContent = 'No more available to add';
        } else if (variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue') {
          this.quantityStock.textContent = `${this.quantityRules.max} available to add`;
        } else {
          this.quantityStock.textContent = '';
        }
      }
    }

    setQuantity(value, showClampError = false) {
      if (!this.quantityInput) return;

      let quantity = Number(value);
      if (!Number.isFinite(quantity)) quantity = this.quantityRules.min;

      if (this.quantityRules.max <= 0) {
        quantity = 0;
      } else {
        quantity = Math.max(this.quantityRules.min, quantity);

        /* Keep quantity aligned to Shopify's quantity increment. */
        const offset = quantity - this.quantityRules.min;
        quantity = this.quantityRules.min + Math.round(offset / this.quantityRules.step) * this.quantityRules.step;

        if (quantity > this.quantityRules.max) {
          quantity = this.quantityRules.max;
          if (showClampError) this.showQuantityError(`Maximum quantity available is ${this.quantityRules.max}.`);
        } else {
          this.hideQuantityError();
        }
      }

      this.quantityInput.value = quantity;
      this.updateQuantityButtons();
      this.updateButtonState();
    }

    updateQuantityButtons() {
      const value = Number(this.quantityInput?.value || 0);
      if (this.quantityMinus) this.quantityMinus.disabled = this.quantityRules.max <= 0 || value <= this.quantityRules.min;
      if (this.quantityPlus) this.quantityPlus.disabled = this.quantityRules.max <= 0 || value >= this.quantityRules.max;
    }

    showQuantityError(message) {
      if (!this.quantityError) return;
      this.quantityError.textContent = message || 'Please choose a valid quantity.';
      this.quantityError.hidden = false;
    }

    hideQuantityError() {
      if (this.quantityError) this.quantityError.hidden = true;
    }

    reset() {
      this.mode = this.data.has_subscription ? 'subscription' : 'one-time';
      this.currentVariant = null;
      this.currentAllocation = null;
      this.currentPlanId = '';
      this.variantHasSubscription = false;
      this.pendingVariantId = null;
      this.quantityRules = { min: 1, max: 1, step: 1 };
      this.setSubmitting(false);
      this.hideQuantityError();

      if (this.quantityWrap) this.quantityWrap.hidden = this.requiresVariantSelection;

      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = '';
        this.sellingPlanInput.disabled = true;
      }

      if (this.frequencySelect) this.frequencySelect.value = '';
      if (this.frequencyPopularHint) {
        this.frequencyPopularHint.textContent = '';
        this.frequencyPopularHint.hidden = true;
      }

      if (this.image && this.data.initial_image) {
        this.image.src = this.data.initial_image;
        this.image.alt = this.data.title || '';
      }

      if (this.requiresVariantSelection) {
        if (this.variantSelect) this.variantSelect.value = '';
        if (this.variantInput) {
          this.variantInput.value = '';
          this.variantInput.disabled = true;
        }
        if (this.availability) this.availability.hidden = true;
        if (this.quantityWrap) this.quantityWrap.hidden = true;
        if (this.purchaseOptions) this.purchaseOptions.hidden = true;
        if (this.frequencyWrap) this.frequencyWrap.hidden = true;
        if (this.summaryRow) this.summaryRow.hidden = true;
        this.setVariantRequired(true);
        this.setFrequencyRequired(false);
        this.updateButtonState();
        return;
      }

      const initialVariant = this.findVariant(this.data.initial_variant_id);
      if (initialVariant) this.setVariant(initialVariant.id, false);
    }

    clearVariant() {
      this.currentVariant = null;
      this.currentAllocation = null;
      this.currentPlanId = '';
      this.variantHasSubscription = false;

      if (this.variantInput) {
        this.variantInput.value = '';
        this.variantInput.disabled = true;
      }
      if (this.availability) this.availability.hidden = true;
      if (this.quantityWrap) this.quantityWrap.hidden = true;
      this.hideQuantityError();
      if (this.purchaseOptions) this.purchaseOptions.hidden = true;
      if (this.frequencyWrap) this.frequencyWrap.hidden = true;
      if (this.frequencySelect) this.frequencySelect.value = '';
      if (this.frequencyPopularHint) {
        this.frequencyPopularHint.textContent = '';
        this.frequencyPopularHint.hidden = true;
      }
      if (this.summaryRow) this.summaryRow.hidden = true;
      this.clearSellingPlan();
      this.setVariantRequired(true);
      this.setFrequencyRequired(false);
      this.updateButtonState();
    }

    setVariant(id, userChanged) {
      const variant = this.findVariant(id);
      if (!variant) {
        this.clearVariant();
        return;
      }

      this.currentVariant = variant;
      this.currentPlanId = '';
      this.currentAllocation = null;

      if (this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = false;
      }
      this.applyQuantityRules(variant);

      if (this.image && variant.image) {
        this.image.src = variant.image;
        this.image.alt = this.data.title || '';
      }

      if (this.availability) {
        this.availability.hidden = !variant.available;
        this.availability.textContent = variant.available ? '✓ In stock' : 'Sold out';
      }

      if (this.oneTimeChoicePrice) this.oneTimeChoicePrice.textContent = this.formatMoney(variant.price);

      const allocations = this.ordergrooveAllocations(variant);
      this.variantHasSubscription = this.data.has_subscription && allocations.length > 0 && variant.available;

      if (this.purchaseOptions) this.purchaseOptions.hidden = !this.variantHasSubscription;
      if (this.subscriptionChoice) this.subscriptionChoice.hidden = !this.variantHasSubscription;

      this.setVariantRequired(false);

      if (this.variantHasSubscription) {
        if (userChanged || !['one-time', 'subscription'].includes(this.mode)) this.mode = 'subscription';
        this.populateFrequencyDropdown();
        this.setMode(this.mode, true);
      } else {
        this.mode = 'one-time';
        if (this.frequencyWrap) this.frequencyWrap.hidden = true;
        this.setFrequencyRequired(false);
        this.clearSellingPlan();
        this.setMode('one-time', true);
      }

      if (this.summaryRow) this.summaryRow.hidden = false;
      this.updatePriceSummary();
      this.updateButtonState();
    }

    populateFrequencyDropdown() {
      if (!this.frequencySelect || !this.currentVariant) return;

      this.currentPlanId = '';
      this.currentAllocation = null;
      this.frequencySelect.innerHTML = '<option value="">Please select a frequency</option>';
      this.frequencySelect.value = '';

      const plans = this.sortedPlans();
      const recommended = normaliseFrequency(this.currentVariant.recommended_frequency);
      let recommendedLabel = '';

      plans.forEach((plan) => {
        const planId = String(plan.id);
        const planFrequency = plan.frequency || plan.name;
        const label = frequencyLabel(planFrequency);
        const isRecommended = Boolean(
          recommended && normaliseFrequency(planFrequency) === recommended
        );

        const option = document.createElement('option');
        option.value = planId;
        option.textContent = isRecommended ? `${label} (Most Popular)` : label;
        option.dataset.frequencyValue = planFrequency;

        if (isRecommended) {
          option.dataset.mostPopular = 'true';
          recommendedLabel = label;
        }

        this.frequencySelect.appendChild(option);
      });

      if (this.frequencyPopularHint) {
        if (recommendedLabel) {
          this.frequencyPopularHint.textContent = `Most popular: ${recommendedLabel}`;
          this.frequencyPopularHint.hidden = false;
        } else {
          this.frequencyPopularHint.textContent = '';
          this.frequencyPopularHint.hidden = true;
        }
      }

      this.setFrequencyRequired(true);
      this.updateSubscriptionChoicePreview();
      this.clearSellingPlan();
    }

    selectFrequency(planId) {
      if (!this.currentVariant || !planId) return;

      this.currentPlanId = String(planId);
      this.currentAllocation = this.ordergrooveAllocations(this.currentVariant)
        .find((allocation) => String(allocation.plan_id) === this.currentPlanId) || null;

      if (this.frequencySelect) this.frequencySelect.value = this.currentPlanId;

      this.setFrequencyRequired(false);
      this.applySellingPlan();
      this.updateSubscriptionChoicePreview();
      this.updatePriceSummary();
      this.updateButtonState();
    }

    setMode(mode, silent = false) {
      if (mode === 'subscription' && !this.variantHasSubscription) mode = 'one-time';

      this.mode = mode;
      this.oneTimeChoice?.classList.toggle('is-selected', mode === 'one-time');
      this.subscriptionChoice?.classList.toggle('is-selected', mode === 'subscription');
      this.oneTimeChoice?.setAttribute('aria-pressed', mode === 'one-time' ? 'true' : 'false');
      this.subscriptionChoice?.setAttribute('aria-pressed', mode === 'subscription' ? 'true' : 'false');

      if (this.frequencyWrap) {
        this.frequencyWrap.hidden = mode !== 'subscription' || !this.variantHasSubscription;
      }

      if (mode === 'one-time') {
        this.clearSellingPlan();
        this.setFrequencyRequired(false);
      } else {
        if (this.currentPlanId) {
          this.applySellingPlan();
          this.setFrequencyRequired(false);
        } else {
          this.setFrequencyRequired(true);
        }
      }

      this.updateSubscriptionChoicePreview();
      this.updatePriceSummary();
      this.updateButtonState();

      if (!silent && mode === 'subscription' && !this.currentPlanId) {
        this.frequencyWrap?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    updateSubscriptionChoicePreview() {
      if (!this.currentVariant) return;

      const repeatPrice = this.currentPlanId
        ? this.priceForPlan(this.currentVariant, this.currentPlanId)
        : this.fallbackSubscriptionPrice(this.currentVariant);
      const { saving, percent } = this.savingDetails(this.currentVariant.price, repeatPrice);

      if (this.subscriptionChoicePrice) this.subscriptionChoicePrice.textContent = this.formatMoney(repeatPrice);
      if (this.subscriptionSave) this.subscriptionSave.textContent = percent > 0 ? `Save ${percent}%` : 'Repeat';
      if (this.subscriptionChoiceSaving) {
        this.subscriptionChoiceSaving.textContent = saving > 0 ? `Save ${this.formatMoney(saving)} every order` : '';
        this.subscriptionChoiceSaving.hidden = saving <= 0;
      }
    }

    applySellingPlan() {
      if (!this.sellingPlanInput || this.mode !== 'subscription' || !this.currentPlanId) return;
      this.sellingPlanInput.disabled = false;
      this.sellingPlanInput.value = this.currentPlanId;
    }

    clearSellingPlan() {
      if (!this.sellingPlanInput) return;
      this.sellingPlanInput.value = '';
      this.sellingPlanInput.disabled = true;
      this.currentAllocation = null;
    }

    updatePriceSummary() {
      if (!this.currentVariant || !this.summaryRow) return;

      const regularPrice = Number(this.currentVariant.price || 0);
      let activePrice = regularPrice;
      let saving = 0;

      if (this.mode === 'subscription' && this.variantHasSubscription) {
        activePrice = this.currentPlanId
          ? this.priceForPlan(this.currentVariant, this.currentPlanId)
          : this.fallbackSubscriptionPrice(this.currentVariant);
        saving = Math.max(regularPrice - activePrice, 0);
      }

      if (this.summaryPrice) this.summaryPrice.textContent = this.formatMoney(activePrice);

      if (this.summaryOriginal) {
        this.summaryOriginal.textContent = this.formatMoney(regularPrice);
        this.summaryOriginal.hidden = saving <= 0;
      }

      if (this.summarySaving) {
        const percent = regularPrice > 0 ? Math.round((saving / regularPrice) * 100) : 0;
        this.summarySaving.textContent = saving > 0
          ? `Save ${this.formatMoney(saving)}${percent > 0 ? ` (${percent}%)` : ''}`
          : '';
        this.summarySaving.hidden = saving <= 0;
      }
    }

    setVariantRequired(required) {
      if (!this.variantField) return;
      this.variantField.classList.toggle('is-required', required);
      if (this.variantError) this.variantError.hidden = !required;
    }

    setFrequencyRequired(required) {
      if (!this.frequencyWrap) return;
      const active = required && this.mode === 'subscription' && this.variantHasSubscription;
      this.frequencyWrap.classList.toggle('is-required', active);
      if (this.frequencyError) this.frequencyError.hidden = !active;
    }

    updateButtonState() {
      if (!this.addButton || !this.addButtonText) return;

      let disabled = false;
      let label = this.data.button_label || 'Add to basket';

      if (!this.currentVariant) {
        disabled = true;
        label = this.data.select_variant_label || 'Select an option';
      } else if (!this.currentVariant.available) {
        disabled = true;
        label = 'Sold out';
      } else if (this.quantityRules.max <= 0 || Number(this.quantityInput?.value || 0) <= 0) {
        disabled = true;
        label = 'No more available';
      } else if (this.mode === 'subscription' && this.variantHasSubscription && !this.currentPlanId) {
        disabled = true;
        label = 'Select a frequency';
      }

      this.addButton.disabled = disabled;
      this.addButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.addButtonText.textContent = label;
    }

    validateBeforeSubmit() {
      if (!this.currentVariant) {
        this.setVariantRequired(true);
        this.variantSelect?.focus();
        this.updateButtonState();
        return false;
      }

      if (!this.currentVariant.available) return false;

      const quantity = Number(this.quantityInput?.value || 0);
      if (this.quantityRules.max <= 0 || quantity < this.quantityRules.min || quantity > this.quantityRules.max) {
        const message = this.quantityRules.max <= 0
          ? 'No more stock is available to add.'
          : `Please choose a quantity between ${this.quantityRules.min} and ${this.quantityRules.max}.`;
        this.showQuantityError(message);
        this.quantityWrap?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.quantityInput?.focus();
        this.updateButtonState();
        return false;
      }

      if (this.mode === 'subscription' && this.variantHasSubscription && !this.currentPlanId) {
        this.setFrequencyRequired(true);
        this.frequencyWrap?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.frequencySelect?.focus();
        this.updateButtonState();
        return false;
      }

      return true;
    }
  }

  if (!customElements.get('psm-quick-buy')) {
    customElements.define('psm-quick-buy', PSMQuickBuy);
  }
})();
